import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useCallback, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

import { useComposableFilter } from '@/components/ComposableFilter';
import Icon from '@/components/Icon/Icon';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { EditBottomSheet } from '@/features/contract-and-cost/EditBottomSheet';
import {
  PaymentOverviewFilterBar,
  type PaymentOverviewViewMode,
  type ComparisonTarget,
} from '@/features/contract-and-cost/payment';
import {
  getMonthsInPeriod,
  parsePeriodStart,
} from '@/features/contract-and-cost/period';
import {
  MonthlyEditGrid,
  type MonthlyEditGridHandle,
} from '@/features/device/contract-and-cost/components/MonthlyEditGrid';
import { DeviceContractType } from '@/features/device/contract-and-cost/components/MonthlyEditGrid/types';
import { PaymentStatCards } from '@/features/device/contract-and-cost/components/PaymentStatCards';
import { PaymentSummaryChart } from '@/features/device/contract-and-cost/components/PaymentSummaryChart';
import { handleApiError } from '@/lib/api/error';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';

import { PaymentOverviewTable } from './-components/PaymentOverviewTable';
import { useSaveDevicePaymentMonthly } from './-mutations';
import {
  useDevicePaymentOverviewList,
  useDevicePaymentOverviewSummary,
} from './-query';

const SearchSchema = z.object({
  viewMode: z.enum(['monthly', 'transition']).default('transition'),
  month: z.number().min(1).max(12).optional(),
  freeText: z.string().optional(),
  pageIndex: z.number().default(0),
  pageSize: z.number().default(100),
  comparisonTarget: z
    .enum(['PREVIOUS_YEAR', 'PREVIOUS_MONTH'])
    .default('PREVIOUS_MONTH'),
});

export const Route = createFileRoute(
  '/_staff/device-catalog/active/$id/_layout/contract-and-cost/_contract-and-cost/payment/$period/',
)({
  component: PaymentOverviewPage,
  validateSearch: zodValidator(SearchSchema),
});

function PaymentOverviewPage() {
  const { id, period } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const { t, i18n } = useTranslation(defaultNS, {
    keyPrefix: 'pages.devices.contractAndCost.paymentOverview',
  });
  const { t: tCommon } = useTranslation(defaultNS, {
    keyPrefix: 'features.contractAndCost',
  });
  const { toast } = useToast();

  const isMonthly = search.viewMode === 'monthly';
  const [editOpen, setEditOpen] = useState(false);
  const [paymentEditGridKey, setPaymentEditGridKey] = useState(0);
  const monthOptions = useMemo(() => getMonthsInPeriod(period), [period]);

  const contracts = useMemo(
    () =>
      Object.values(DeviceContractType).map((type) => ({
        id: type,
        label: t(`table.contractType.${type}`),
      })),
    [t],
  );

  const paymentEditGridRef = useRef<MonthlyEditGridHandle>(null);

  const savePaymentMutation = useSaveDevicePaymentMonthly(id, monthOptions, {
    onSuccess: () => {
      toast({ title: tCommon('editBottomSheet.saveSuccessToast') });
      setEditOpen(false);
    },
    onError: (error) => {
      const { message } = handleApiError(error, i18n);
      toast({ title: message, variant: 'destructive' });
    },
  });
  const currentMonth = search.month ?? new Date().getMonth() + 1;

  const [showSummary, setShowSummary] = useState(false);
  const summaryRegionId = 'payment-overview-summary-region';

  const filter = useComposableFilter<Record<string, never>>({
    filterDefs: [],
    filterState: {},
    onFilterStateChange: () => {},
  });

  const parsedPeriodStart = parsePeriodStart(period);
  const periodDate = new Date(
    Date.UTC(parsedPeriodStart.year, parsedPeriodStart.month - 1, 1),
  );

  const { data: summary } = useDevicePaymentOverviewSummary(id, {
    targetYearMonth: periodDate.toISOString(),
  });
  const transitionPurchaseQuery = useDevicePaymentOverviewList(id, {
    targetYearMonth: periodDate.toISOString(),
    paymentComparisonTarget: search.comparisonTarget,
    contractType: 'PURCHASE',
  });
  const transitionLeaseQuery = useDevicePaymentOverviewList(id, {
    targetYearMonth: periodDate.toISOString(),
    paymentComparisonTarget: search.comparisonTarget,
    contractType: 'LEASE',
  });

  const handleViewModeChange = useCallback(
    (mode: PaymentOverviewViewMode) => {
      navigate({
        search: (prev) => ({
          ...prev,
          viewMode: mode,
          month: mode === 'monthly' ? currentMonth : undefined,
        }),
        replace: true,
      });
    },
    [navigate, currentMonth],
  );

  const handleMonthChange = useCallback(
    (month: number) => {
      navigate({
        search: (prev) => ({ ...prev, month }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleComparisonTargetChange = useCallback(
    (target: ComparisonTarget) => {
      navigate({
        search: (prev) => ({ ...prev, comparisonTarget: target }),
      });
    },
    [navigate],
  );

  return (
    <div className="relative flex flex-1 flex-col gap-5 overflow-auto px-12 py-5 min-h-0">
      <div id={summaryRegionId} hidden={!showSummary}>
        {summary && (
          <VStack className="gap-5">
            <PaymentStatCards summary={summary} />
            <PaymentSummaryChart data={summary.months} months={monthOptions} />
          </VStack>
        )}
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          className="flex items-center gap-1 transition-colors "
          aria-expanded={showSummary}
          aria-controls={summaryRegionId}
          onClick={() => setShowSummary((prev) => !prev)}
        >
          <Icon
            iconName={showSummary ? 'ChevronUpIcon' : 'ChevronDownIcon'}
            className={cn('size-3.5')}
          />
          <Text
            className={cn(
              'text-labelSmallReg',
              'text-neutral-muted-on-fill',
              'hover:text-neutral-on-fill',
            )}
          >
            {showSummary
              ? tCommon('paymentOverview.summary.collapse')
              : tCommon('paymentOverview.summary.expand')}
          </Text>
        </button>
      </div>

      <PaymentOverviewFilterBar
        viewMode={search.viewMode}
        onViewModeChange={handleViewModeChange}
        selectedMonth={isMonthly ? currentMonth : undefined}
        onSelectedMonthChange={handleMonthChange}
        monthOptions={monthOptions}
        filter={filter}
        comparisonTarget={search.comparisonTarget}
        onComparisonTargetChange={handleComparisonTargetChange}
      />

      <HStack className="justify-end">
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 text-labelSmall"
          onClick={() => {
            setPaymentEditGridKey((k) => k + 1);
            setEditOpen(true);
          }}
        >
          <Icon iconName="PencilIcon" className="size-3.5" />
          {tCommon('editButton')}
        </Button>
      </HStack>

      <EditBottomSheet
        open={editOpen}
        onOpenChange={setEditOpen}
        title={tCommon('editBottomSheet.editPaymentTitle')}
        onSave={() => {
          const rows = paymentEditGridRef.current?.getAllContractData();
          if (!rows?.length) return;
          savePaymentMutation.mutate(rows);
        }}
        onCancel={() => setEditOpen(false)}
      >
        <MonthlyEditGrid
          key={paymentEditGridKey}
          ref={paymentEditGridRef}
          months={monthOptions}
          contracts={contracts}
          dataList={[transitionPurchaseQuery.data, transitionLeaseQuery.data]
            .filter((data) => data != null)
            .map((data) => ({
              contractType: data.contractType,
              months: data.months.map((month) => ({
                targetYearMonth: month.targetYearMonth,
                plannedAmount: month.plannedPaymentAmount,
                actualAmount: month.actualPaymentAmount,
                quantity: month.quantity,
              })),
            }))}
        />
      </EditBottomSheet>

      {!isMonthly && (
        <PaymentOverviewTable
          purchaseData={transitionPurchaseQuery.data}
          leaseData={transitionLeaseQuery.data}
          isPending={
            transitionPurchaseQuery.isPending || transitionLeaseQuery.isPending
          }
          months={monthOptions}
          className="flex-1 min-h-0"
        />
      )}
    </div>
  );
}
